'use client';

import { useTranslations } from 'next-intl';
import React, { useState, useMemo, useRef } from 'react';

import DentalTreatmentCatalogFiltration from '@/app-components/resouces/bg-dpc/DentalTreatmentCatalogFiltration';
import ItemDentalTreatmentInteractive from "@/app-components/resouces/bg-dpc/ItemDentalTreatmentInteractive";
import ResourceMarkdown from '@/app-components/resouces/ResourceMarkdown';
import { BgDpcCodeType } from '@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcCodeType';
import { BgDpcRecipeCategoryType } from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcRecipeCategoryType";
import { BgDpcRecipeType } from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcRecipeType";

interface SearchFormDentalTreatmentsProps {
	codes: BgDpcCodeType[];
	treatments: BgDpcRecipeType[];
	treatmentCategories: BgDpcRecipeCategoryType[];
}

export default function SearchFormDentalTreatments({ treatments, treatmentCategories, codes }: SearchFormDentalTreatmentsProps) {
	const MAX_ITEMS = 3;
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [searchQuery, setSearchQuery] = useState(''); // State for search query
	const [showAllItems, setShowAllItems] = useState(false); // State to toggle all items visibility
	const t = useTranslations('page.r-bg-dental-procedure-codes');
	const treatmentsRef = useRef<HTMLDivElement>(null); // Ref for the treatments container

	// Compute filtered treatments based on selectedCategory and searchQuery
	const filteredTreatments = useMemo(() => {
		// Filter by category
		let filtered = selectedCategory === 'all' 
			? treatments 
			: treatments.filter((treatmentItem) => treatmentItem.category.slug === selectedCategory);

		// Further filter by search query
		if (searchQuery.trim()) {
			const lowerQuery = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(treatmentItem) =>
					treatmentItem.name.toLowerCase().includes(lowerQuery) ||
					treatmentItem.description.toLowerCase().includes(lowerQuery)
			);
		}

		return filtered;
	}, [selectedCategory, searchQuery, treatments]);

	// Limit the displayed treatments based on the `showAllItems` flag
	const displayedTreatments = showAllItems ? filteredTreatments : filteredTreatments.slice(0, MAX_ITEMS);

	const handleToggle = () => {
		setShowAllItems(!showAllItems);

		// Scroll to the treatments container, only when showing less.
		if (showAllItems && treatmentsRef.current) {
			treatmentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	return (
		<section className="pt-0 pb-0">
			<div className="container position-relative z-index-1 mb-5 mb-lg-7 pb-sm-6">
				<div className="row">
					<div className="col-lg-10 col-xl-9 text-center mx-auto">
						<h2 className="mb-4 h2">{t('dental_treatments_section.title')}</h2>

						<div className="bg-light border rounded-3 position-relative px-4 py-3 mt-5">
							<form className="row g-3 justify-content-center align-items-center">
								<div className="col-lg-6">
									<div className="position-relative">
										{/* Search Input */}
										<input
											className="form-control form-control-lg me-1 ps-3"
											type="text"
											placeholder={t('dental_treatments_section.search_placeholder')}
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)} // Update searchQuery
										/>
									</div>
								</div>

								<div className="col-lg-6">
									<div className="position-relative">
										{/* Category Filter */}
										<DentalTreatmentCatalogFiltration
											treatmentCategories={treatmentCategories}
											onCategoryChange={setSelectedCategory}
											className="form-select form-select-lg ps-3"
										/>
									</div>
								</div>
							</form>
						</div>

						<div className="small mt-3">
							<ResourceMarkdown>
								{t('dental_treatments_section.popular_searches')}
							</ResourceMarkdown>
						</div>
					</div>
				</div>

				<div className="row g-2 g-xl-4 pt-3" id="treatments" ref={treatmentsRef}>
					{displayedTreatments.length > 0 ? (
						displayedTreatments.map((treatmentItem) => (
							<div className="col-12" key={treatmentItem.slug}>
								<ItemDentalTreatmentInteractive
									codes={codes}
									treatment={treatmentItem}
									treatments={treatments}
									searchQuery={searchQuery}
								/>
							</div>
						))
					) : (
						<div className="col-12 text-center muted">
							<ResourceMarkdown>
								{t('common_used.no-results', { search: searchQuery })}
							</ResourceMarkdown>
						</div>
					)}
				</div>

				{/* Toggle Link */}
				{filteredTreatments.length > MAX_ITEMS && (
					<div className="text-center mt-4">
						<button
							className="btn btn-link heading-color text-decoration-underline"
							onClick={handleToggle}
						>
							{showAllItems ? (
								<>
									{t('common_used.show_fewer_results')}
									<i className="bi bi-chevron-up ms-1"></i>
								</>
							) : (
								<>
									{t('common_used.see_all', { results_number: filteredTreatments.length - MAX_ITEMS })}
									<i className="bi bi-chevron-down ms-1"></i>
								</>
							)}
						</button>
					</div>
				)}
			</div>
		</section>
	);
}
